import React, { useState } from 'react';
import './FeedbackButton.css';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3030';

interface FeedbackFormData {
  name: string;
  email: string;
  phone: string;
  message: string;
  rating: number;
}

const FeedbackButton: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  
  const [formData, setFormData] = useState<FeedbackFormData>({
    name: '',
    email: '',
    phone: '',
    message: '',
    rating: 0
  });

  const [hoveredRating, setHoveredRating] = useState(0);

  const handleOpenModal = () => {
    setIsModalOpen(true);
    setSubmitStatus(null);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSubmitStatus(null);
    // Reset form after a delay to allow animation
    setTimeout(() => {
      setFormData({
        name: '',
        email: '',
        phone: '',
        message: '',
        rating: 0
      });
    }, 300);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleRatingClick = (rating: number) => {
    setFormData(prev => ({ ...prev, rating }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name || !formData.email || !formData.message || formData.rating === 0) {
      setSubmitStatus({
        type: 'error',
        message: 'Please fill in all required fields and select a rating'
      });
      return;
    }

    if (formData.message.length < 10) {
      setSubmitStatus({
        type: 'error',
        message: 'Feedback message must be at least 10 characters'
      });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        setSubmitStatus({
          type: 'success',
          message: data.message || 'Thank you for your feedback!'
        });
        
        // Close modal after 2 seconds
        setTimeout(() => {
          handleCloseModal();
        }, 2000);
      } else {
        setSubmitStatus({
          type: 'error',
          message: data.message || 'Failed to submit feedback'
        });
      }
    } catch (error) {
      setSubmitStatus({
        type: 'error',
        message: 'Network error. Please try again later.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Floating Feedback Button */}
      <button
        className="feedback-floating-button"
        onClick={handleOpenModal}
        aria-label="Open Feedback Form"
      >
        <span className="feedback-icon">💬</span>
        <span className="feedback-text">Feedback</span>
      </button>

      {/* Modal Overlay */}
      {isModalOpen && (
        <div className="feedback-modal-overlay" onClick={handleCloseModal}>
          <div className="feedback-modal" onClick={(e) => e.stopPropagation()}>
            <div className="feedback-modal-header">
              <h2>We Value Your Feedback</h2>
              <button
                className="feedback-modal-close"
                onClick={handleCloseModal}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="feedback-form">
              {/* Rating Stars */}
              <div className="feedback-form-group">
                <label className="feedback-label">
                  Rating <span className="required">*</span>
                </label>
                <div className="feedback-rating">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      className={`feedback-star ${
                        star <= (hoveredRating || formData.rating) ? 'active' : ''
                      }`}
                      onClick={() => handleRatingClick(star)}
                      onMouseEnter={() => setHoveredRating(star)}
                      onMouseLeave={() => setHoveredRating(0)}
                      aria-label={`${star} star${star > 1 ? 's' : ''}`}
                    >
                      ★
                    </button>
                  ))}
                  {formData.rating > 0 && (
                    <span className="feedback-rating-text">
                      {formData.rating}/5
                    </span>
                  )}
                </div>
              </div>

              {/* Name */}
              <div className="feedback-form-group">
                <label htmlFor="feedback-name" className="feedback-label">
                  Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="feedback-name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Your name"
                  required
                  className="feedback-input"
                />
              </div>

              {/* Email */}
              <div className="feedback-form-group">
                <label htmlFor="feedback-email" className="feedback-label">
                  Email <span className="required">*</span>
                </label>
                <input
                  type="email"
                  id="feedback-email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="your.email@example.com"
                  required
                  className="feedback-input"
                />
              </div>

              {/* Phone (Optional) */}
              <div className="feedback-form-group">
                <label htmlFor="feedback-phone" className="feedback-label">
                  Phone Number <span className="optional">(Optional)</span>
                </label>
                <input
                  type="tel"
                  id="feedback-phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="+1 (555) 123-4567"
                  className="feedback-input"
                />
              </div>

              {/* Message */}
              <div className="feedback-form-group">
                <label htmlFor="feedback-message" className="feedback-label">
                  Your Feedback <span className="required">*</span>
                </label>
                <textarea
                  id="feedback-message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder="Tell us what you think... (minimum 10 characters)"
                  required
                  rows={5}
                  className="feedback-textarea"
                  maxLength={2000}
                />
                <div className="feedback-char-count">
                  {formData.message.length}/2000 characters
                </div>
              </div>

              {/* Status Messages */}
              {submitStatus && (
                <div className={`feedback-message feedback-message-${submitStatus.type}`}>
                  {submitStatus.type === 'success' ? '✓ ' : '⚠ '}
                  {submitStatus.message}
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                className="feedback-submit-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default FeedbackButton;
